import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { currentUserLookupIdSelector } from 'redux/selectors';
import oWnApi from 'utils/WnApi';
import LoadingZone from 'components/LoadingZone';
import Accordion from '../../Layout/Accordion';
import philanthropyReportDataNormalizer from './apiDataNormalizer';
import FundOverview from './FundOverview';
import FundFinancialHistory from './FundFinancialHistory';
import FundFinancialChart from './FundFinancialChart';
import { fnHasFinancialChartData } from './helpers';
import Recipient from './Recipient';

const PhilanthropyReport = () => {
  const [aFundData, fnSetFundData] = useState([]);
  const [isReportDataLoading, setIsReportDataLoading] = useState(true);
  const sLookupId = useSelector(currentUserLookupIdSelector);

  useEffect(() => {
    const fetchPhilanthropyData = async () => {
      try {
        const result = await oWnApi.get(
          `constituents/${sLookupId}/reports/philanthropy`
        );
        const validatedData = await philanthropyReportDataNormalizer.validate(
          result.data
        );
        fnSetFundData(validatedData.STEWARDSHIPDATA.SCHOLARSHIPS);
        setIsReportDataLoading(false);
      } catch (err) {
        console.error('Error fetching philanthropy report data', err);
      }
    };
    fetchPhilanthropyData();
  }, [sLookupId]);

  return (
    <Accordion
      mTitle='Philanthropy Report'
      sTitleIcon='bookOpen'
      sGtmCategory='Philanthropy Report'
    >
      <LoadingZone isLoading={isReportDataLoading}>
        <div className='philanthropyReport__content'>
          {aFundData.map((oFund) => {
            const {
              CONTACTS,
              FUNDNAME,
              DESIGNATIONLEVELID,
              RECIPIENTS,
              FINANCIALS,
            } = oFund;
            const currentRecipients = RECIPIENTS.filter(
              (recipient) => recipient.STATUS === 'CURRENT'
            );
            const formerRecipients = RECIPIENTS.filter(
              (recipient) => recipient.STATUS === 'FORMER'
            );
            const bHasChartData = fnHasFinancialChartData(FINANCIALS);
            return (
              <Accordion
                mTitle={FUNDNAME}
                sSize='medium'
                className='philanthropyReport__scholarship'
                key={DESIGNATIONLEVELID}
                sGtmCategory='Philanthropy Report: Scholarship Fund'
              >
                {CONTACTS.length > 0 && <FundOverview aContacts={CONTACTS} />}
                {bHasChartData && (
                  <FundFinancialChart oFinancialData={FINANCIALS[0]} />
                )}
                <div className='philanthropyReport__scholarshipContent'>
                  {currentRecipients.length > 0 && (
                    <Accordion
                      mTitle='Current Recipients'
                      sSize='small'
                      sGtmCategory='Philanthropy Report: Scholarship Recipients'
                    >
                      <div className='philanthropyReport__recipientsContent'>
                        {currentRecipients.map((oRecipient) => (
                          <Recipient
                            data={oRecipient}
                            key={oRecipient.LOOKUPID}
                          />
                        ))}
                      </div>
                    </Accordion>
                  )}
                  {formerRecipients.length > 0 && (
                    <Accordion
                      mTitle='Former Recipients'
                      sSize='small'
                      sGtmCategory='Philanthropy Report: Scholarship Recipients'
                    >
                      <div className='philanthropyReport__recipientsContent'>
                        {formerRecipients.map((oRecipient) => (
                          <Recipient
                            data={oRecipient}
                            key={oRecipient.LOOKUPID}
                          />
                        ))}
                      </div>
                    </Accordion>
                  )}
                </div>
                {FINANCIALS.length > 0 && (
                  <FundFinancialHistory
                    aData={FINANCIALS}
                    sFundType={oFund.FUND_TYPE.DESCRIPTION}
                  />
                )}
              </Accordion>
            );
          })}
        </div>
      </LoadingZone>
    </Accordion>
  );
};

export default PhilanthropyReport;
